import { useEffect, useState, useMemo } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { Box, Tab, Tabs, Button } from "@mui/material";
import { usePortfolio } from "../hooks/usePortfolio";
import { useLivePrices } from "../hooks/useLivePrices";
import {
  calculatePnlPercentage,
  classNames,
  formatPrice,
} from "../utils/utils";
import useDrawerStore from "store/DrawerStore";
import TradingSymbol from "components/TradingSymbol";
import { usePortfolioStore } from "../store/usePortfolioStore";
import { useAuth } from "../hooks/useAuth";
import ZerodhaAuthAlert from "../components/ZerodhaAuthAlert";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function PortfolioPage() {
  const { prices, getLivePrice } = useLivePrices();
  const { holdings, positions, stats, fetchPortfolio, updateStats, isInitialized } = usePortfolioStore();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { user } = useAuth();

  useEffect(() => {
    if (!isInitialized && user?.isZerodhaLinked) {
      fetchPortfolio();
    }
  }, [isInitialized, user?.isZerodhaLinked]);

  useEffect(() => {
    if (isInitialized) {
      const tokens = [
        ...holdings.map((h) => h.instrument_token),
        ...positions.map((p) => p.instrument_token),
      ].filter((token) => token);
      if (tokens.length > 0) getLivePrice(tokens);
    }
  }, [holdings, positions, isInitialized]);

  useEffect(() => {
    updateStats(prices);
  }, [prices, updateStats]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "tradingsymbol",
      headerName: "Symbol",
      flex: 0.5,
      renderCell: (params) => (
        <TradingSymbol symbol={params.row.tradingsymbol} />
      ),
    },
    {
      field: "sector",
      headerName: "Sector",
      flex: 0.4,
      renderCell: (params) => (
        <span style={{ fontSize: '11px', color: '#64748b' }}>{params.value || "-"}</span>
      ),
    },
    {
      field: "industry",
      headerName: "Industry",
      flex: 0.5,
      renderCell: (params) => (
        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{params.value || "-"}</span>
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      flex: 0.5,
      renderCell: (params) => {
        const mainQty = params.row.quantity;
        const t1Qty = params.row.t1_quantity || 0;
        if (t1Qty > 0) {
          return (
            <span>
              {mainQty} <span style={{ fontSize: '10px', color: '#64748b' }}> (+{t1Qty} T1)</span>
            </span>
          );
        }
        return mainQty;
      }
    },
    {
      field: "average_price",
      headerName: "Avg Price",
      // type: "number",
      flex: 0.5,
      renderCell: (params) => params.value.toFixed(2),
    },
    {
      field: "ltp",
      headerName: "LTP",
      // type: "number",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.instrument_token]?.price;
        return (
          <span
            className={
              params.row.close_price > (livePrice || params.row.last_price)
                ? "priceDown"
                : "priceUp"
            }
          >
            {livePrice || params.row.last_price}
          </span>
        );
      },
    },
    {
      field: "today",
      headerName: "Today",
      flex: 0.5,
      renderCell: (params) => {
        const liveData = prices[params.row.instrument_token];
        const livePrice = liveData?.price;
        const liveChange = liveData?.change;
        const ltp = livePrice || params.row.last_price;
        
        let changeAmount = 0;
        let pnlPercent: string;

        if (params.row.t1_quantity !== undefined) {
          // Holding
          const totalQty = params.row.quantity + (params.row.t1_quantity || 0);
          changeAmount = (ltp - params.row.close_price) * totalQty;
        } else {
          // Position
          changeAmount = params.row.day_pnl || 0;
        }

        if (liveChange !== undefined) {
          pnlPercent = liveChange.toFixed(1);
        } else {
          const baseline = params.row.close_price || params.row.average_price || 0;
          pnlPercent = calculatePnlPercentage(ltp, baseline);
        }

        return (
          <div className={classNames(changeAmount >= 0 ? "upColor" : "downColor", "percentCt")}>
            {formatPrice(changeAmount)}
            <span
              style={{ fontSize: "10px", paddingLeft: "10px", textAlign: "right", float: "right" }}
              className={changeAmount >= 0 ? "percentUp" : "percentDown"}
            >
              {pnlPercent}
            </span>
          </div>
        );
      },
    },
    {
      field: "pnl",
      headerName: "P&L",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.instrument_token]?.price;
        const ltp = livePrice || params.row.last_price;
        
        let pnl = 0;
        if (params.row.t1_quantity !== undefined) {
          // Holding
          const totalQty = params.row.quantity + (params.row.t1_quantity || 0);
          pnl = (ltp - params.row.average_price) * totalQty;
        } else {
          // Position
          pnl = params.row.pnl || 0;
        }

        return (
          <div className={classNames(pnl >= 0 ? "upColor" : "downColor", "percentCt")}>
            {formatPrice(pnl)}
            <span
              style={{ fontSize: "10px", paddingLeft: "10px", textAlign: "right", float: "right" }}
              className={pnl >= 0 ? "percentUp" : "percentDown"}
            >
              {calculatePnlPercentage(ltp, params.row.average_price)}
            </span>
          </div>
        );
      },
    },
    {
      field: "peRatio",
      headerName: "PE",
      flex: 0.3,
      renderCell: (params) => (
        <span style={{ fontWeight: params.value && params.value < 25 ? 700 : 400 }}>
          {params.value ? params.value.toFixed(1) : "-"}
        </span>
      ),
    },
    {
      field: "roe",
      headerName: "ROE",
      flex: 0.35,
      renderCell: (params) => (
        <span style={{ 
          color: params.value && params.value > 0.15 ? '#22c55e' : 'inherit',
          fontWeight: params.value && params.value > 0.20 ? 700 : 400
        }}>
          {params.value ? `${(params.value * 100).toFixed(1)}%` : "-"}
        </span>
      ),
    },
    {
      field: "debtToEquity",
      headerName: "D/E",
      flex: 0.3,
      renderCell: (params) => (
        <span style={{ 
          color: params.value && params.value > 2 ? '#ef4444' : 'inherit',
          fontWeight: params.value && params.value < 1 ? 700 : 400
        }}>
          {params.value ? params.value.toFixed(2) : "-"}
        </span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      renderCell: (params) => (
        <Button variant="outlined" size="small" color="error">
          Sell
        </Button>
      ),
    },
  ], [prices]);

  const holdingsData = holdings.map((h) => ({
    ...h,
    id: h.tradingsymbol,
  }));

  const positionsData = positions.map((p) => ({
    ...p,
    id: p.tradingsymbol,
  }));

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      {!user?.isZerodhaLinked && <ZerodhaAuthAlert />}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange} aria-label="portfolio tabs">
          <Tab label="Holdings" {...a11yProps(0)} />
          <Tab label="Positions" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 className="text-2xl font-bold mb-4">Holdings</h4>
            <div className={`p-2 rounded ${stats.todayPNL >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <span className="text-gray-600 mr-2">Today P&L:</span>
              <span className={`font-bold ${stats.todayPNL >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {formatPrice(stats.todayPNL)}
              </span>
            </div>
          </div>
          <DataGrid
            rows={holdingsData}
            columns={columns}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
              sorting: { sortModel: [{ field: "pnl", sort: "desc" }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "primary.main",
                width: "auto",
              },
            }}
          />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h4 className="text-2xl font-bold mb-4">Positions</h4>
          </div>
          <DataGrid
            rows={positionsData}
            columns={columns}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
              sorting: { sortModel: [{ field: "unrealised", sort: "desc" }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "primary.main",
                width: "auto",
              },
            }}
          />
        </div>
      </CustomTabPanel>
    </Box>
  );
}
